import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Mail } from "lucide-react";

import { Alert, AuthPanel, Button } from "@h2j-data-team/ui";

import { authClient } from "@/lib/auth-client";
import { PRODUCT_NAME } from "@repo/shared";

export const Route = createFileRoute("/_auth/verify-email")({
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === "string" ? search.email : undefined,
  }),
  head: () => ({ meta: [{ title: `Verify email — ${PRODUCT_NAME}` }] }),
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { email } = Route.useSearch();
  const { t } = useTranslation("auth");
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleResend = async () => {
    if (!email) return;
    setResendState("sending");
    const result = await authClient.sendVerificationEmail({
      email,
      callbackURL: `${window.location.origin}/verify-email/confirm`,
    });
    if (result.error) {
      setResendState("error");
    } else {
      setResendState("sent");
    }
  };

  return (
    <AuthPanel
      icon={<Mail />}
      title={t("verifyEmail.heading")}
      description={
        email ? (
          <Trans
            i18nKey="verifyEmail.descriptionWithEmail"
            ns="auth"
            values={{ email }}
            components={{ strong: <strong /> }}
          />
        ) : (
          t("verifyEmail.description")
        )
      }
      actions={
        email ? (
          <Button
            size="lg"
            variant="outline"
            onClick={handleResend}
            loading={resendState === "sending"}
            disabled={resendState === "sent"}
          >
            {resendState === "sending"
              ? t("verifyEmail.sending")
              : resendState === "sent"
                ? t("verifyEmail.emailSent")
                : t("verifyEmail.resend")}
          </Button>
        ) : undefined
      }
      footer={
        <span>
          {t("verifyEmail.wrongAccount")}{" "}
          <Link
            to="/login"
            search={{
              mode: undefined,
              token: undefined,
              error: undefined,
              redirect: undefined,
              email: undefined,
            }}
          >
            {t("verifyEmail.signInDifferent")}
          </Link>
        </span>
      }
    >
      {resendState === "sent" && <Alert tone="success">{t("verifyEmail.resent")}</Alert>}
      {resendState === "error" && <Alert tone="danger">{t("verifyEmail.resendFailed")}</Alert>}
    </AuthPanel>
  );
}
